<template>
  <div class="console-page">
    <!-- 时间段筛选 -->
    <el-card class="filter-card" shadow="never">
      <div class="fg-row">
        <div class="fg-item">
          <span class="fg-label">统计区间</span>
          <el-date-picker
            v-model="range"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :picker-options="pickerOptions"
            style="width: 300px"
          />
        </div>
        <el-button type="primary" icon="el-icon-search" @click="reload">查询</el-button>
        <el-button icon="el-icon-refresh" @click="resetRange">本月</el-button>
        <span class="fg-hint">账单/退款状态卡与订单/账单金额按录入时间归期，核算业绩按审核归期</span>
      </div>
    </el-card>

    <!-- 账单状态卡 -->
    <el-card class="block-card" shadow="never">
      <div slot="header" class="block-head">
        <span class="block-title">账单状态</span>
        <span class="block-sub">按录入时间 · {{ rangeText }}</span>
      </div>
      <div class="card-grid bill-grid">
        <div v-for="c in billCards" :key="c.key" class="mini-card" :class="'tone-' + c.tone" @click="gotoBills(c.status)">
          <div class="mini-value">{{ fmtNum(cardData[c.key]) }}</div>
          <div class="mini-label">
            <i :class="'el-icon-' + c.icon"></i>
            <span>{{ c.label }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 退款状态卡 -->
    <el-card class="block-card" shadow="never">
      <div slot="header" class="block-head">
        <span class="block-title">退款状态</span>
        <span class="block-sub">按录入时间 · {{ rangeText }}</span>
      </div>
      <div class="card-grid refund-grid">
        <div v-for="c in refundCards" :key="c.key" class="mini-card" :class="'tone-' + c.tone" @click="gotoRefunds(c.status)">
          <div class="mini-value">{{ fmtNum(cardData[c.key]) }}</div>
          <div class="mini-label">
            <i :class="'el-icon-' + c.icon"></i>
            <span>{{ c.label }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 业绩概况 -->
    <el-card class="block-card" shadow="never">
      <div slot="header" class="block-head">
        <span class="block-title">业绩概况</span>
        <span class="block-sub">{{ rangeText }}</span>
      </div>
      <div class="sum-grid">
        <div class="sum-item" v-for="s in sumItems" :key="s.key">
          <div class="sum-value" :class="'sum-tone-' + s.tone">{{ fmtMoney(summary[s.key]) }}</div>
          <div class="sum-label">{{ s.label }}</div>
        </div>
      </div>
      <div class="sum-note">
        <span>核算业绩按审核归期（perform_date），退款业绩为已完成退款的冲减额。</span>
      </div>
    </el-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { orderConsole } from '@/api/stats'

export default {
  name: 'OrderConsole',
  data() {
    const now = new Date()
    return {
      range: [this.fmt(new Date(now.getFullYear(), now.getMonth(), 1)), this.fmt(now)],
      pickerOptions: {
        shortcuts: [
          { text: '本月', onClick: p => { const d = new Date(); p.$emit('pick', [this.fmt(new Date(d.getFullYear(), d.getMonth(), 1)), this.fmt(d)]) } },
          { text: '近30天', onClick: p => { const e = new Date(); const s = new Date(e.getTime() - 29 * 86400000); p.$emit('pick', [this.fmt(s), this.fmt(e)]) } },
          { text: '近90天', onClick: p => { const e = new Date(); const s = new Date(e.getTime() - 89 * 86400000); p.$emit('pick', [this.fmt(s), this.fmt(e)]) } }
        ]
      },
      cardData: { overdue: 0, refused: 0, pending: 0, completed: 0, unbilled: 0, rp: 0, rc: 0, rr: 0 },
      summary: {
        order_count: 0, order_amount: 0, bill_total: 0, bill_audited: 0,
        perform_amount: 0, refund_count: 0, refund_amount: 0, refund_perform: 0
      },
      loading: false
    }
  },
  computed: {
    ...mapState(['user']),
    isCompany() {
      return !!(this.user && this.user.type === 'company')
    },
    rangeText() {
      return this.range && this.range.length === 2 ? `${this.range[0]} ~ ${this.range[1]}` : ''
    },
    billCards() {
      return [
        { key: 'overdue', label: '已逾期', status: 5, icon: 'time', tone: 'red' },
        { key: 'refused', label: '审核失败', status: 3, icon: 'circle-close', tone: 'violet' },
        { key: 'pending', label: '待审核', status: 2, icon: 'document', tone: 'orange' },
        { key: 'completed', label: '已完成', status: 4, icon: 'circle-check', tone: 'green' },
        { key: 'unbilled', label: '未出账', status: 0, icon: 'files', tone: 'blue' }
      ]
    },
    refundCards() {
      return [
        { key: 'rp', label: '待审核退款', status: 1, icon: 'time', tone: 'orange' },
        { key: 'rc', label: '已完成退款', status: 2, icon: 'circle-check', tone: 'green' },
        { key: 'rr', label: '未通过退款', status: 3, icon: 'circle-close', tone: 'violet' }
      ]
    },
    sumItems() {
      return [
        { key: 'order_count', label: '订单数', tone: 'blue' },
        { key: 'order_amount', label: '订单额', tone: 'blue' },
        { key: 'bill_total', label: '账单总额', tone: 'indigo' },
        { key: 'bill_audited', label: '已审核账单总额', tone: 'indigo' },
        { key: 'perform_amount', label: '核算业绩', tone: 'green' },
        { key: 'refund_count', label: '退款数量', tone: 'red' },
        { key: 'refund_amount', label: '退款金额', tone: 'red' },
        { key: 'refund_perform', label: '退款业绩', tone: 'red' }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    fmt(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    fmtNum(n) {
      return Number(n || 0).toLocaleString('zh-CN')
    },
    fmtMoney(n) {
      return '¥' + Number(n || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    reload() {
      this.loadData()
    },
    resetRange() {
      const now = new Date()
      this.range = [this.fmt(new Date(now.getFullYear(), now.getMonth(), 1)), this.fmt(now)]
      this.loadData()
    },
    loadData() {
      if (this.loading) return
      this.loading = true
      const data = {}
      if (this.range && this.range.length === 2) {
        data.start_date = this.range[0]
        data.end_date = this.range[1]
      }
      orderConsole(data)
        .then(d => {
          const bc = d.bill_cards || {}
          const rc = d.refund_cards || {}
          const sm = d.summary || {}
          this.cardData = {
            overdue: bc.overdue || 0, refused: bc.refused || 0, pending: bc.pending || 0,
            completed: bc.completed || 0, unbilled: bc.unbilled || 0,
            rp: rc.pending || 0, rc: rc.completed || 0, rr: rc.refused || 0
          }
          this.summary = {
            order_count: sm.order_count || 0, order_amount: sm.order_amount || 0,
            bill_total: sm.bill_total || 0, bill_audited: sm.bill_audited || 0,
            perform_amount: sm.perform_amount || 0, refund_count: sm.refund_count || 0,
            refund_amount: sm.refund_amount || 0, refund_perform: sm.refund_perform || 0
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false })
    },
    gotoBills(status) {
      this.$router.push({ path: '/bill/list', query: { status } })
    },
    gotoRefunds(status) {
      this.$router.push({ path: '/refund/list', query: { status } })
    }
  }
}
</script>

<style scoped>
.console-page { padding: 0; }

/* ===== 筛选栏 ===== */
.filter-card {
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid #E5E9F2;
}

.fg-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 18px;
}

.fg-item { display: flex; align-items: center; gap: 8px; }

.fg-label { font-size: 13px; color: #8A94AD; flex-shrink: 0; }

.fg-hint {
  font-size: 12px;
  color: #B4BDCF;
  margin-left: auto;
}

/* ===== 区块卡 ===== */
.block-card {
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid #E5E9F2;
}

.block-head { display: flex; align-items: baseline; gap: 10px; }

.block-title {
  font-size: 15px;
  font-weight: 700;
  color: #18233C;
}

.block-sub { font-size: 12px; color: #8A94AD; }

/* ===== 状态卡 ===== */
.card-grid {
  display: grid;
  gap: 12px;
}

.bill-grid { grid-template-columns: repeat(5, 1fr); }
.refund-grid { grid-template-columns: repeat(3, 1fr); }

.mini-card {
  border-radius: 10px;
  padding: 16px 18px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  border: 1px solid transparent;
}

.mini-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(31, 45, 82, 0.10);
}

.mini-value {
  font-size: 26px;
  font-weight: 700;
  color: #18233C;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.mini-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 12px;
  color: #5A6580;
}

.mini-label i { font-size: 13px; }

.tone-red { background: #FEF0F0; border-color: #FBDCDC; }
.tone-red .mini-label i { color: #E5484D; }
.tone-orange { background: #FFF7EC; border-color: #FCE7C8; }
.tone-orange .mini-label i { color: #E8930C; }
.tone-green { background: #EDF9F2; border-color: #CDECDB; }
.tone-green .mini-label i { color: #16A34A; }
.tone-blue { background: #EDF3FE; border-color: #D0DFFB; }
.tone-blue .mini-label i { color: #3668FC; }
.tone-violet { background: #F4F0FD; border-color: #E4DAFA; }
.tone-violet .mini-label i { color: #7C5CD6; }
.tone-indigo { background: #EEF2FB; border-color: #D7DFF5; }
.tone-indigo .mini-label i { color: #4C5B9E; }

/* ===== 业绩概况 ===== */
.sum-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.sum-item {
  border: 1px solid #E5E9F2;
  border-radius: 10px;
  padding: 16px 18px;
  background: #FBFCFE;
}

.sum-value {
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.sum-label { font-size: 12px; color: #8A94AD; margin-top: 6px; }

.sum-tone-blue { color: #3668FC; }
.sum-tone-indigo { color: #4C5B9E; }
.sum-tone-green { color: #16A34A; }
.sum-tone-red { color: #E5484D; }

.sum-note {
  margin-top: 14px;
  padding: 10px 14px;
  background: #F4F6FB;
  border-radius: 8px;
  font-size: 12px;
  color: #8A94AD;
}

/* ===== Element UI 品牌色统一 ===== */
::v-deep .el-button--primary {
  background: #3668FC;
  border-color: #3668FC;
}

::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }

::v-deep .el-dialog { border-radius: 12px; }

/* ===== 移动端降级 ===== */
@media (max-width: 767px) {
  .bill-grid { grid-template-columns: repeat(2, 1fr); }
  .refund-grid { grid-template-columns: repeat(3, 1fr); }
  .sum-grid { grid-template-columns: repeat(2, 1fr); }
  .fg-hint { display: none; }
  .fg-search { width: 100%; }
}
</style>
